import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Inbox, Check, X, Loader2, ShieldCheck, MessageCircle, Clock,
    User as UserIcon, BookOpen,
} from "lucide-react";
import { toast } from "sonner";

function fmtTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function StatusBadge({ status }) {
    const map = {
        pending: { cls: "border-accent/60 text-accent-foreground bg-accent/15", label: "Pending" },
        approved: { cls: "border-primary/50 text-primary bg-primary/8", label: "Approved" },
        rejected: { cls: "border-destructive/40 text-destructive bg-destructive/8", label: "Rejected" },
    };
    const m = map[status] || map.pending;
    return (
        <Badge variant="outline" className={`${m.cls} uppercase text-[10px] tracking-wider`}>
            {m.label}
        </Badge>
    );
}

function ResourceIcon({ type }) {
    if (type === "program") return <BookOpen className="h-4 w-4 text-primary" />;
    return <UserIcon className="h-4 w-4 text-primary" />;
}

export default function AdminDeleteRequests() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("pending");
    const [decisionTarget, setDecisionTarget] = useState(null); // { request, action: "approve"|"reject" }
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = tab !== "all" ? { status: tab } : {};
            const { data } = await api.get("/admin/delete-requests", { params });
            setRows(data.requests || []);
        } catch (err) {
            toast.error("Could not load delete requests.");
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => {
        const tally = { pending: 0, approved: 0, rejected: 0 };
        rows.forEach((r) => { tally[r.status] = (tally[r.status] || 0) + 1; });
        return tally;
    }, [rows]);

    const openDecision = (request, action) => {
        setDecisionTarget({ request, action });
        setComment("");
    };

    const submitDecision = async () => {
        if (!decisionTarget) return;
        const { request, action } = decisionTarget;
        setSubmitting(true);
        try {
            await api.post(`/admin/delete-requests/${request.id}/${action}`, {
                comment: comment.trim() || undefined,
            });
            toast.success(
                action === "approve"
                    ? `Approved — ${request.resource_label} has been purged.`
                    : `Request from ${request.requested_by_name} declined.`,
            );
            setDecisionTarget(null);
            setComment("");
            await load();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not process the decision.";
            toast.error(typeof msg === "string" ? msg : "Decision failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10 pb-24 space-y-6"
            data-testid="admin-delete-requests"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl text-foreground flex items-center gap-3">
                        <ShieldCheck className="h-7 w-7 text-primary" />
                        Delete Requests
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        {isSuper
                            ? "Admins send purge requests here for your review. Approving immediately purges the record; rejecting leaves it in trash."
                            : "Track your purge requests. Once the super admin reviews them, the result appears here."}
                    </p>
                </div>
                {isSuper && counts.pending > 0 && (
                    <Badge
                        variant="outline"
                        className="border-accent/50 text-accent-foreground bg-accent/15 uppercase text-[10px] tracking-wider"
                    >
                        {counts.pending} awaiting review
                    </Badge>
                )}
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="pending" data-testid="del-tab-pending">
                        Pending {counts.pending > 0 && `(${counts.pending})`}
                    </TabsTrigger>
                    <TabsTrigger value="approved" data-testid="del-tab-approved">
                        Approved {counts.approved > 0 && `(${counts.approved})`}
                    </TabsTrigger>
                    <TabsTrigger value="rejected" data-testid="del-tab-rejected">
                        Rejected {counts.rejected > 0 && `(${counts.rejected})`}
                    </TabsTrigger>
                    <TabsTrigger value="all" data-testid="del-tab-all">All</TabsTrigger>
                </TabsList>
            </Tabs>

            <Card className="surface-card border-border">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="text-center py-16 text-muted-foreground">
                            <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                            Loading…
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="text-center py-16">
                            <Inbox className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
                            <div className="text-sm text-muted-foreground">
                                No {tab !== "all" ? tab : ""} delete requests right now.
                            </div>
                        </div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {rows.map((req) => (
                                <li
                                    key={req.id}
                                    className="p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 hover:bg-muted/30 transition-colors"
                                    data-testid={`del-request-${req.id}`}
                                >
                                    <div className="min-w-0 flex-1 space-y-2">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <ResourceIcon type={req.resource_type} />
                                            <span className="font-medium text-foreground">
                                                {req.resource_label}
                                            </span>
                                            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                                                {req.resource_type}
                                            </Badge>
                                            <StatusBadge status={req.status} />
                                        </div>
                                        <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                                            <MessageCircle className="h-3 w-3" />
                                            <span>
                                                Requested by{" "}
                                                <span className="text-foreground">
                                                    {req.requested_by_name || req.requested_by_email}
                                                </span>
                                            </span>
                                            <Clock className="h-3 w-3 ml-2" />
                                            <span>{fmtTime(req.requested_at)}</span>
                                        </div>
                                        <p className="text-sm text-foreground/80 italic">
                                            "{req.reason}"
                                        </p>
                                        {req.status !== "pending" && (
                                            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
                                                <div className="text-muted-foreground">
                                                    {req.status === "approved" ? "Approved" : "Rejected"} by{" "}
                                                    <span className="text-foreground">{req.decided_by_name}</span>{" "}
                                                    · {fmtTime(req.decided_at)}
                                                </div>
                                                {req.decision_comment && (
                                                    <div className="text-foreground/80 mt-1">
                                                        Note: {req.decision_comment}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {isSuper && req.status === "pending" && (
                                        <div className="flex gap-2 sm:flex-col sm:w-36">
                                            <Button
                                                size="sm"
                                                onClick={() => openDecision(req, "approve")}
                                                data-testid={`del-approve-${req.id}`}
                                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                            >
                                                <Check className="h-3.5 w-3.5 mr-1" />
                                                Approve &amp; purge
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => openDecision(req, "reject")}
                                                data-testid={`del-reject-${req.id}`}
                                            >
                                                <X className="h-3.5 w-3.5 mr-1" />
                                                Decline
                                            </Button>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>

            <Dialog
                open={!!decisionTarget}
                onOpenChange={(o) => { if (!o) { setDecisionTarget(null); setComment(""); } }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {decisionTarget?.action === "approve"
                                ? `Approve & permanently purge "${decisionTarget?.request?.resource_label}"?`
                                : `Decline this delete request?`}
                        </DialogTitle>
                        <DialogDescription>
                            {decisionTarget?.action === "approve"
                                ? "This will execute the purge immediately. The record and all related data will be removed for good."
                                : "The requester will see your note in their Delete Requests list. The record stays in trash and can be restored."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                            Note for the requester (optional)
                        </label>
                        <Textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={3}
                            placeholder="e.g. Confirmed via internal records — proceeding with purge."
                            data-testid="del-decision-note"
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setDecisionTarget(null); setComment(""); }}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={submitDecision}
                            disabled={submitting}
                            data-testid="del-decision-confirm"
                            className={
                                decisionTarget?.action === "approve"
                                    ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    : ""
                            }
                        >
                            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
                            {decisionTarget?.action === "approve" ? "Approve & purge" : "Decline"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
